import { Controller } from '@hotwired/stimulus'

// Connects to data-controller="custom-allowances"
export default class extends Controller {
  static targets = [
    'locationRow',
    'rowTemplate',
    'newRowLocation',
    'addFirstClassDisplay',
    'addMoreClassesDisplay',
    'customContainer',
  ]

  declare readonly locationRowTargets: HTMLDivElement[]
  declare readonly rowTemplateTarget: HTMLTemplateElement
  declare readonly newRowLocationTarget: HTMLDivElement
  declare readonly addFirstClassDisplayTarget: HTMLDivElement
  declare readonly addMoreClassesDisplayTarget: HTMLDivElement
  declare readonly customContainerTarget: HTMLDivElement

  private readonly locationSelectors: Set<HTMLSelectElement> = new Set()
  private readonly usedLocationIds: Set<string> = new Set()
  private rowCount = 0

  updateAddClassButton (): void {
    if (this.rowCount > 0) {
      this.addFirstClassDisplayTarget.classList.add('hidden')
      this.addMoreClassesDisplayTarget.classList.remove('hidden')
    } else {
      this.addFirstClassDisplayTarget.classList.remove('hidden')
      this.addMoreClassesDisplayTarget.classList.add('hidden')
    }
  }

  locationRowTargetConnected (locationRow: HTMLDivElement): void {
    const locationFields = locationRow.querySelectorAll('[id$="location_id"]')
    const locationSelect = locationRow.querySelector<HTMLSelectElement>('select')!

    locationSelect.dataset.locationId = locationSelect.value
    this.usedLocationIds.add(locationSelect.value)
    this.locationSelectors.add(locationSelect)

    locationSelect.addEventListener('change', (e) => this.stateChanged(e, [...locationFields]))

    this.rowCount++

    this.updateAddClassButton()
  }

  stateChanged (event: Event, locationFields: Element[]) {
    if (event.target instanceof HTMLSelectElement) {
      const select = event.target

      if (!this.usedLocationIds.has(select.value) || select.value === select.dataset.locationId) {
        locationFields.forEach((locationField) => {
          const locationId = select.value
          const targetField = locationField as HTMLInputElement
          targetField.value = locationId
        })

        this.usedLocationIds.delete(select.dataset.locationId!)
        this.usedLocationIds.add(select.value)
        select.dataset.locationId = select.value
      } else {
        select.value = select.dataset.locationId!
      }

      if (select.value !== '') {
        select.setCustomValidity('')
        select.reportValidity()
      }
    }
  }

  addRow (): void {
    const newRow = document
      .importNode(this.rowTemplateTarget.content, true)
      .firstElementChild as HTMLDivElement
    const rowHTML = newRow.outerHTML.replace(/NEW_RECORD/g, new Date().getTime().toString())
    this.newRowLocationTarget.insertAdjacentHTML('beforebegin', rowHTML)

    this.customContainerTarget.scrollTop = this.customContainerTarget.scrollHeight

    if (window.segment) {
      window.segment.track('Custom class added')
    }
  }

  removeRow (event: Event): void {
    if (event.target) {
      const target = event.target as HTMLElement
      const rowContainer = target.closest('[data-custom-allowances-target="locationRow"]')! as HTMLDivElement
      const locationSelect = rowContainer.querySelector<HTMLSelectElement>('select')!

      this.usedLocationIds.delete(locationSelect.value)

      if (rowContainer.dataset.newRecord === 'true') {
        rowContainer.remove()
      } else {
        rowContainer.querySelectorAll('[id$="_destroy"]').forEach((destroyField) => {
          const destroyInput = destroyField as HTMLInputElement
          destroyInput.value = '1'
        })

        // Reset invalid amount inputs to 0 before hiding, otherwise validations will complain
        // the input is not focusable
        rowContainer.querySelectorAll('input[type=number]').forEach((amountField) => {
          const amountInput = amountField as HTMLInputElement
          if (!amountInput.validity.valid) {
            amountInput.value = '0'
          }
        })

        rowContainer.classList.add('hidden')
      }

      this.rowCount--
    }

    this.updateAddClassButton()
  }

  submitAllowances (event: Event): void {
    event.preventDefault()

    let valid = true
    if (event.target instanceof HTMLFormElement) {
      this.locationSelectors.forEach((locationSelect) => {
        if (locationSelect.value === '') {
          locationSelect.setCustomValidity("Location can't be blank")
          locationSelect.reportValidity()
          valid = false
        }
      })

      if (valid) {
        event.target.submit()
      }
    }
  }
}
